/* eslint-disable no-extra-parens */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { reduxConnect } from '../../common';
import type { GlobalAdConfigs, IStoreState, Slot } from '../../components/common/interfaces/types';
import { useDevice } from '../../components/common/utilities/useDevice';
import { AdDisplayWidget } from '../../components/ads/adDisplayWidget';
import { DisplayAdProvider } from '../../common/providers/displayAdProvider';
import { useInViewRef, useDocumentVisibilityState } from 'rooks';
import { demandManagerRequestSingleSlot } from '../../common/providers/demandManagerBidRequests';
import { setPrebidErrorEventListeners } from './adManagerUtils';
import { isPageVisible } from '../../common/utilities/pageVisibilityChange';

export const adVisibilityThreshold = 0.5;

export const intersectionObserverOptions: IntersectionObserverInit = {
    threshold: adVisibilityThreshold, // 0.5 ==> 50% is the representation of the percentage of the target element which is visible: value between 0.0 and 1.0 (trade off at 0.3 to compensate for ad request time - we may modify this value)
    root: null, // To watch for intersections relative to the device's viewport
    rootMargin: '0px' // Margin around the root. These values serves to grow or shrink each side of the root element's bounding box before computing intersections.
};

type DisplayAdUnitProps = {
    slotId: string;
    width: number;
    height: number;
    currentRoute: string;
    refreshAdEnabled: boolean;
    pageAdsConfigured: boolean;
    adsConfigured: boolean;
    renderDisplayAdForSlot: (...args: any[]) => void;
    enableAds: boolean;
    showOnPhone: boolean;
    showOnTablet: boolean;
    showOnDesktop: boolean;
    refreshAd: (...args: any[]) => void;
    adRefreshIntervalSeconds: number;
    leadingEdgeDebounceDelay: number;
    mapsPageRefreshTimer: number;
    isInfiniteScrollAd: boolean;
    displayInfiniteScrollAd: (...args: any[]) => void;
    infiniteScrollIndex: number;
    currentConfiguredPageId: string;
    currentConfiguredLatitude: number;
    currentConfiguredLongitude: number;
    pageId: string;
    pageName: string;
    busDomain: string;
    latitude: number;
    longitude: number;
    pageVisibility: boolean;
    refreshWatchParam: number | string;
    ignoreVisibility: boolean;
    globalAdConfig: GlobalAdConfigs;
    showDefaultGDPRUser: boolean;
};

const MINIMUM_ALLOWED_TIME_BETWEEN_AD_REFRESHES = 3000; // 3s;

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { appState: rootAppState, legal } = storeState || {};
    const { appState } = rootAppState || {};
    const { pageId, pageName, busDomain, enableAds, leadingEdgeDebounceDelay, defaultAdRefeshIntervalSeconds, mapsPageRefreshTimer } = appState || {};
    const { displayAd } = storeState;
    const locality = appState?.user?.ActiveLocation?.Locality || {};
    const userIsBot = appState?.user?.IsBot;
    const complianceRequirements = legal?.complianceRequirements;
    const showDefaultGDPRUser = complianceRequirements && complianceRequirements?.requiresGDPRCompliance && complianceRequirements?.disableAds;

    return {
        currentRoute: storeState?.currentRoute,
        pageAdsConfigured: displayAd?.pageAdsConfigured,
        adsConfigured: displayAd?.adsConfigured,
        currentConfiguredPageId: displayAd?.currentConfiguredPageId,
        currentConfiguredLatitude: displayAd?.currentConfiguredLatitude,
        currentConfiguredLongitude: displayAd?.currentConfiguredLongitude,
        adRefreshIntervalSeconds: defaultAdRefeshIntervalSeconds || 300,
        leadingEdgeDebounceDelay: leadingEdgeDebounceDelay || 3,
        mapsPageRefreshTimer: mapsPageRefreshTimer || 60,
        enableAds: !userIsBot && enableAds,
        showDefaultGDPRUser: showDefaultGDPRUser,
        globalAdConfig: displayAd?.adConfig,
        pageId,
        pageName,
        busDomain,
        latitude: locality?.Latitude,
        longitude: locality?.Longitude
    };
};

const mapDispatchToProps = {};

export const DisplayAdUnitWrapper = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({
    currentRoute,
    pageAdsConfigured,
    adsConfigured,
    currentConfiguredPageId,
    currentConfiguredLatitude,
    currentConfiguredLongitude,
    adRefreshIntervalSeconds,
    showOnDesktop = true,
    showOnPhone = true,
    showOnTablet = true,
    enableAds,
    showDefaultGDPRUser,
    globalAdConfig = { pageAdConfigs: [] },
    pageId,
    latitude,
    longitude,
    refreshWatchParam,
    slotId,
    width,
    height,
    isInfiniteScrollAd,
    infiniteScrollIndex
}: DisplayAdUnitProps) => {
    const [ref, inView] = useInViewRef(intersectionObserverOptions);
    const pageVisibility = useDocumentVisibilityState();
    const [isAdGlobalsReady, setIsAdGlobalsReady] = useState(false);
    const [adLoadedState, setAdLoadedState] = useState(false);
    const [adAvailableState, setAdAvailableState] = useState(false);
    const [adErrorState, setAdErrorState] = useState(false);
    const globalsRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isAdVisibleRef = useRef(false);
    const adRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const adUnitRef = useRef<HTMLDivElement | null>(null);
    const lastEventRefreshRef = useRef(0);
    const adInitializedRef = useRef(false);
    const prevProps = useRef<Partial<{
        pageId: string;
        latitude: number;
        longitude: number;
        currentConfiguredPageId: string;
        currentConfiguredLatitude: string;
        currentConfiguredLongitude: string;
        pageVisibility: boolean;
        refreshWatchParam: string | number;
    }>>({});
    const retryRef = useRef<{
        lastEventRefresh: number;
        retryCount: number;
        maxRetries: number;
        retryDelay:number;
    }>({
        lastEventRefresh: 0,
        maxRetries: 3,
        retryCount: 0,
        retryDelay: 350
    });

    const { isDesktop, isMobile, isTablet } = useDevice();

    const stopProcessingUnit = useMemo(() => {
        if ((isMobile && !showOnPhone)
            || (isTablet && !showOnTablet)
            || (isDesktop && !showOnDesktop)) {
            return true;
        }
        return false;
    }, [isMobile, isDesktop, isTablet, showOnDesktop, showOnPhone, showOnTablet]);

    const {
        pageAdConfig,
        placementCfg,
        placementId
    } = useMemo(() => {
        if (showDefaultGDPRUser || !slotId || !pageId || !adsConfigured || !pageAdsConfigured || stopProcessingUnit) {
            return {
                pageAdConfig: undefined,
                placementCfg: undefined,
                placementId: ''
            };
        }
        const pageAdConfig = (globalAdConfig?.pageAdConfigs?.find(pageConfig => pageConfig?.pageId?.toLowerCase() === pageId?.toLowerCase()) || { slots: [] });
        const placementCfg = pageAdConfig?.slots?.find(x => x.slotId === slotId);
        const placementId = placementCfg?.tagId || '';

        return {
            pageAdConfig,
            placementCfg,
            placementId
        };
    }, [
        stopProcessingUnit,
        globalAdConfig?.pageAdConfigs?.[0]?.pageId, 
        slotId, 
        pageId,
        adsConfigured,
        pageAdsConfigured,
        showDefaultGDPRUser,
        enableAds]);

    // registered ad unit slotId
    const registeredSlotId = isInfiniteScrollAd && Number.isFinite(infiniteScrollIndex) ? `${slotId}${infiniteScrollIndex}` : slotId;

    const checkRequiredGlobalObjects = useCallback(() => {
        if (!enableAds || !adsConfigured || !slotId || stopProcessingUnit || isAdGlobalsReady) {
            return;
        }

        if (window?.pbjs?.rp && window?.pbjs?.que && window?.apntag?.anq) {
            setIsAdGlobalsReady(true);
            retryRef.current.retryCount = 0;
            globalsRefreshTimeoutRef.current = null;
            return;
        }

        if (retryRef?.current?.retryCount < retryRef?.current?.maxRetries) {
            retryRef.current.retryCount++;
            globalsRefreshTimeoutRef.current = setTimeout(checkRequiredGlobalObjects, retryRef.current.retryDelay);
        } else {
            // Perform logging here
            // missing required global
            if (window?.pbjs && window?.apntag && !window?.pbjs?.rp) {
                // Demand Manager failed to load after 3 retries
                console.warn('WB::WEB::LEGACY::ADS Demand Manager failed to load after 3 retries');
            } else {
                console.warn('WB::WEB::LEGACY::ADS Some globals failed to load after 3 retries');
            }

            retryRef.current.retryCount = 0;
            globalsRefreshTimeoutRef.current = null;
        }
    }, [enableAds, adsConfigured, pageAdsConfigured, slotId, stopProcessingUnit, isAdGlobalsReady]);

    const handleXandrOnAdLoadEvent = useCallback(() => {
        setAdLoadedState(true);
        setAdErrorState(false);
    }, []);
    const handleXandrOnAdAvailableEvent = useCallback(() => {
        setAdAvailableState(true);
    }, []);
    const handleXandrOnAdErrorEvent = useCallback((adError: any, adObj: any) => {
        console.warn('WB::WEB::LEGACY::ADS calling handleXandrOnAdErrorEvent ad Error:', slotId);
        
        const analytics = getAnalytics?.();
    
        if (!analytics) {
            return;
        }
        logEvent?.(analytics, 'legacy_xandr_ad_error', {
            slot_id: registeredSlotId,
            target_id: adError?.targetId,
            ad_error_code: adError?.code,
            message: adError?.message,
            creative_id: adObj?.creativeId,
            width: adObj?.width,
            height: adObj?.height,
            ad_type: adObj?.adType
        });
    }, []);

    const handleXandrAdRequestFailureEvent = useCallback((adError: any, adObj: any) => {
        console.warn('WB::WEB::LEGACY::ADS calling handleXandrAdRequestFailureEvent ad Request Failure:', slotId);
        setAdErrorState(true);
        setAdLoadedState(false);

        const analytics = getAnalytics?.();
    
        if (!analytics) {
            return;
        }
        logEvent?.(analytics, 'legacy_xandr_ad_request_error', {
            slot_id: registeredSlotId,
            target_id: adError?.targetId,
            ad_error_code: adError?.code,
            message: adError?.message,
            creative_id: adObj?.creativeId,
            width: adObj?.width,
            height: adObj?.height,
            ad_type: adObj?.adType
        });
    }, []);
    
    const handleXandrAdBadRequestEvent = useCallback((adError: any) => {
        console.warn('WB::WEB::LEGACY::ADS calling handleXandrAdBadRequestEvent ad Bad Request:', slotId);
        setAdErrorState(true);
        setAdLoadedState(false);

        const analytics = getAnalytics?.();
    
        if (!analytics) {
            return;
        }
        logEvent?.(analytics, 'legacy_xandr_ad_bad_request', {
            slot_id: registeredSlotId,
            ad_error_code: adError?.code,
            target_id: adError?.targetId,
            message: adError?.message
        });
    }, []);

    const handleXandrAdNoBidEvent = useCallback((adObj: any) => {
        console.warn('WB::WEB::LEGACY::ADS calling handleXandrAdNoBidEvent No Bid Request:', slotId);
        setAdErrorState(true);
        setAdLoadedState(false);
        const analytics = getAnalytics?.();
    
        if (!analytics) {
            return;
        }
        logEvent?.(analytics, 'legacy_xandr_ad_no_bid', {
            slot_id: registeredSlotId,
            target_id: adObj?.targetId,
            message: 'no bid'
        });
    }, []);

    // prebid event callbacks
    const adRenderFailedEventCallback = useCallback((reason: string, message: string) => {
        // log error here.
        const analytics = getAnalytics?.();
    
        if (!analytics) {
            return;
        }
        logEvent?.(analytics, 'legacy_xandr_ad_prebid_ad_render_failed', {
            slot_id: registeredSlotId,
            message: reason
        });
        console.warn('WB::WEB::LEGACY::ADS FAILED TO RENDER AD ---', reason, message);
    }, []);

    const cancelAdRefresh = useCallback(() => {
        if (adRefreshTimeoutRef.current) {
            clearInterval(adRefreshTimeoutRef.current);
            adRefreshTimeoutRef.current = null;
        }
    }, []);

    const refreshAd = (isFullRefresh?: boolean) => {
        if (!isAdVisibleRef.current || stopProcessingUnit || !adUnitRef.current || !registeredSlotId || !placementId || !adsConfigured || !pageAdsConfigured) {
            return;
        }

        if (!window.pbjs?.rp || !window?.apntag?.anq) {
            console.error('WB::WEB::LEGACY::ADS @@@ missing globals, skipping ad refresh');
            return;
        }

        if (document?.visibilityState === 'hidden') {
            return;
        }

        if (adUnitRef.current?.innerHTML) {
            adUnitRef.current.innerHTML = '';   
        }

        new DisplayAdProvider().refreshAdV2(slotId, pageId, globalAdConfig, pageAdConfig, isInfiniteScrollAd, registeredSlotId, isFullRefresh).then(() => {
            // Do nothing for now
            lastEventRefreshRef.current = Date.now();
            if (isAdVisibleRef.current) {
                setAdAvailableState(false);
                setAdErrorState(false);
                setAdLoadedState(false);
            }
        }).catch((err) => {
            // log
            console.error(`WB::WEB::LEGACY::ADS Failed to refresh ad with slotId: ${slotId}`, err);
        });
    };

    const scheduleAdRefresh = () => {
        cancelAdRefresh();
        if (!isAdVisibleRef.current || !slotId || !pageId || !placementCfg || !placementId || !adsConfigured || !pageAdsConfigured || stopProcessingUnit) {
            return;
        }
        if (registeredSlotId === 'x03') {
            // prevent refreshes on skin ads
            return;
        }
        if (adRefreshTimeoutRef.current) {
            cancelAdRefresh();
        }
        
        if (document?.visibilityState === 'hidden') {
            cancelAdRefresh();
            return;
        }

        const refreshIntervalSeconds = adRefreshIntervalSeconds ? adRefreshIntervalSeconds * 1000 : 60 * 1000;
        adRefreshTimeoutRef.current = setInterval(() => {
            // refresh Ad
            if (!isAdVisibleRef.current) {
                cancelAdRefresh();
                return;
            }
            refreshAd(true);
        }, refreshIntervalSeconds);
    };

    const handleAdRefreshEvent = (isFullRefresh?: boolean) => {
        if (stopProcessingUnit) {
            return;
        }
        const timeNow = Date.now();
        if (isAdVisibleRef.current && timeNow - lastEventRefreshRef.current > MINIMUM_ALLOWED_TIME_BETWEEN_AD_REFRESHES) {
            refreshAd(isFullRefresh);
            scheduleAdRefresh();
        }
    };

    const initializeAdUnit = () => {
        if (!registeredSlotId || !placementCfg || !placementId || !adsConfigured || !pageAdsConfigured || !enableAds || stopProcessingUnit || !adUnitRef.current) {
            return;
        }

        if (registeredSlotId === 'x03' && pageId && pageId !== 'forecasts/current') {
            // only initialize banner ads on Home page
            return;
        }

        if (adInitializedRef.current) {
            return;
        }

        if (!window?.apntag?.anq || !window?.pbjs?.rp || !window?.pbjs?.que) {
            // Log error missing required globals
        }

        const getASTTag = window?.apntag?.getTag(registeredSlotId);

        if (!isInfiniteScrollAd) {
            if (!getASTTag) {
                const slotMap = [] as any[];
                const adSlot = { 
                    name: placementCfg.slotId, 
                    sizes: [] as any[]
                };
                placementCfg.adProviderConfig.sizes.forEach(function(size) {
                    adSlot.sizes.push({ 
                        w: size[0],
                        h: size[1]
                    });
                });
    
                slotMap.push(adSlot);
                // define tags that might not have been defined initially before registering events.
                new DisplayAdProvider().defineAdSlotTag(placementCfg, globalAdConfig);
    
                demandManagerRequestSingleSlot(slotMap);
            } else if (getASTTag?.utCalled && !getASTTag?.showTagCalled) {
                // when 
                // - ad request has been sent (common for ads units below the fold)
                // - ad isn't in view
                // - but there is an available ad
                loadAd(registeredSlotId);
                scheduleAdRefresh();
            } else if (!getASTTag?.utCalled && !getASTTag?.showTagCalled) {
                // when 
                // - tag has been defined
                // - ad request /ut still in-flight
                // - 
                window.apntag?.onEvent?.('adLoaded', registeredSlotId, handleXandrOnAdLoadEvent);
                window.apntag?.onEvent?.('adAvailable', registeredSlotId, handleXandrOnAdAvailableEvent);
                window.apntag?.onEvent?.('adNoBid', registeredSlotId, handleXandrAdNoBidEvent);
                window.apntag?.onEvent?.('adError', registeredSlotId, handleXandrOnAdErrorEvent);
                window.apntag?.onEvent?.('adRequestFailure', registeredSlotId, handleXandrAdRequestFailureEvent);
                window.apntag?.onEvent?.('adBadRequest', registeredSlotId, handleXandrAdBadRequestEvent);
            }
        } else {
            loadInfiniteScrollAd(globalAdConfig, placementCfg, registeredSlotId);
        }

        adInitializedRef.current = true;

    };

    const loadInfiniteScrollAd = (globalAdConfig: GlobalAdConfigs, placementCfg: Slot, registeredSlotId: string) => {
        new DisplayAdProvider().displayInfiniteScrollAdAction(registeredSlotId, placementCfg, globalAdConfig).then(() => {
            if (isAdVisibleRef.current) {
                // do nothing here for now
            } else {
                console.warn('WB::WEB::LEGACY::ADS Skipping refresh for Ad unit that is longer in view.', registeredSlotId);
            }
        });
        window.apntag?.onEvent?.('adLoaded', registeredSlotId, handleXandrOnAdLoadEvent);
        window.apntag?.onEvent?.('adAvailable', registeredSlotId, handleXandrOnAdAvailableEvent);
        window.apntag?.onEvent?.('adNoBid', registeredSlotId, handleXandrAdNoBidEvent);
        window.apntag?.onEvent?.('adError', registeredSlotId, handleXandrOnAdErrorEvent);
        window.apntag?.onEvent?.('adRequestFailure', registeredSlotId, handleXandrAdRequestFailureEvent);
        window.apntag?.onEvent?.('adBadRequest', registeredSlotId, handleXandrAdBadRequestEvent);
    };
    
    const renderInfiniteScrollAd = (registeredSlotId: string) => {
        if (registeredSlotId) {
            window?.apntag?.showTag?.(registeredSlotId);
        }
    };
    
    const loadAd = (registeredSlotId: string) => {
        new DisplayAdProvider().renderDisplayAdForSlot(registeredSlotId).then(() => {
            if (isAdVisibleRef.current) {
                // do nothing for now
            } else {
                console.warn('WB::WEB::LEGACY::ADS Skipping refresh for an Ad unit that is no longer in view.', registeredSlotId);
            }
        });
    };

    useEffect(() => {
        if (isAdVisibleRef.current) {
            checkRequiredGlobalObjects(); 
        }
    }, [checkRequiredGlobalObjects]);

    useEffect(() => {
        if (pageAdsConfigured && isAdGlobalsReady && isAdVisibleRef.current && !adInitializedRef.current) {
            initializeAdUnit();
        }
    }, [isAdGlobalsReady, pageAdsConfigured]);

    useEffect(() => {
        isAdVisibleRef.current = inView;
        
        if (adInitializedRef.current) {
            if (inView && !adRefreshTimeoutRef.current) {
                scheduleAdRefresh();
            } else if (!inView && adRefreshTimeoutRef.current) {
                cancelAdRefresh();
            }
        } else if (!adInitializedRef.current && inView && !adRefreshTimeoutRef.current) {
            initializeAdUnit();
        }
    }, [inView]);

    useEffect(() => {
        if (!isAdVisibleRef.current || !adInitializedRef.current || !registeredSlotId) {
            return;
        }

        if (pageId && currentConfiguredPageId && pageId !== currentConfiguredPageId) {
            if (registeredSlotId === 'wb_banner') {
                cancelAdRefresh();
                handleAdRefreshEvent(true);
            } else {
                cancelAdRefresh();
                initializeAdUnit();
            }
        } 
    }, [pageId, currentConfiguredPageId, registeredSlotId]);
    
    useEffect(() => {
        if (!isAdVisibleRef.current || !adInitializedRef.current) {
            return;
        }
        if (Number.isFinite(latitude)
            && Number.isFinite(longitude)
            && Number.isFinite(currentConfiguredLatitude)
            && Number.isFinite(currentConfiguredLongitude) 
            && (latitude !== currentConfiguredLatitude) 
            || (longitude !== currentConfiguredLongitude)) {
            handleAdRefreshEvent(true);
        }
    }, [latitude, currentConfiguredLatitude, longitude, currentConfiguredLongitude]);
    
    useEffect(() => {
        if (!isAdVisibleRef.current || !adInitializedRef.current) {
            return;
        }
        if (refreshWatchParam
            && prevProps.current.refreshWatchParam
            && (prevProps.current.refreshWatchParam !== refreshWatchParam)) {
            handleAdRefreshEvent(true);
        }
        prevProps.current.refreshWatchParam = refreshWatchParam;
    }, [refreshWatchParam]);
    
    useEffect(() => {
        if (!isAdVisibleRef.current || !adInitializedRef.current) {
            return;
        }
        if (pageVisibility
            && typeof prevProps.current.pageVisibility === 'boolean'
            && (prevProps.current.pageVisibility !== isPageVisible(pageVisibility))) {
            handleAdRefreshEvent();
        } else if (!isPageVisible(pageVisibility) && prevProps.current.pageVisibility) {
            cancelAdRefresh();
        } else if (!isPageVisible(pageVisibility) && prevProps.current.pageVisibility === undefined) {
            cancelAdRefresh();  
        }
        prevProps.current.pageVisibility = isPageVisible(pageVisibility);
    }, [pageVisibility]);

    useEffect(() => {
        if (stopProcessingUnit || !registeredSlotId) {
            return;
        }

        if (!adAvailableState ) {
            return;
        }

        if (!isAdVisibleRef.current) {
            return;
        }
        if (!adInitializedRef.current) {
            return;
        }

        if (document?.visibilityState === 'hidden') {
            return;
        }
        
        if (isInfiniteScrollAd) {
            renderInfiniteScrollAd(registeredSlotId);
        } else {
            loadAd(registeredSlotId);
        }
        scheduleAdRefresh();
        
    }, [adAvailableState, isInfiniteScrollAd, registeredSlotId, stopProcessingUnit]);

    useEffect(() => {
        // AST event listeners
        window.apntag?.onEvent?.('adLoaded', registeredSlotId, handleXandrOnAdLoadEvent);
        window.apntag?.onEvent?.('adAvailable', registeredSlotId, handleXandrOnAdAvailableEvent);
        window.apntag?.onEvent?.('adNoBid', registeredSlotId, handleXandrAdNoBidEvent);
        window.apntag?.onEvent?.('adError', registeredSlotId, handleXandrOnAdErrorEvent);
        window.apntag?.onEvent?.('adRequestFailure', registeredSlotId, handleXandrAdRequestFailureEvent);
        window.apntag?.onEvent?.('adBadRequest', registeredSlotId, handleXandrAdBadRequestEvent);

        // Prebid event listeners
        setPrebidErrorEventListeners('on', adRenderFailedEventCallback);
        return () => {
            cancelAdRefresh();
            window.apntag?.offEvent?.('adLoaded', registeredSlotId, handleXandrOnAdLoadEvent);
            window.apntag?.offEvent?.('adAvailable', registeredSlotId, handleXandrOnAdAvailableEvent);
            window.apntag?.offEvent?.('adNoBid', registeredSlotId, handleXandrAdNoBidEvent);
            window.apntag?.offEvent?.('adError', registeredSlotId, handleXandrOnAdErrorEvent);
            window.apntag?.offEvent?.('adRequestFailure', registeredSlotId, handleXandrAdRequestFailureEvent);
            window.apntag?.offEvent?.('adBadRequest', registeredSlotId, handleXandrAdBadRequestEvent);

            // Prebid event listeners
            setPrebidErrorEventListeners('off', adRenderFailedEventCallback);
        };
    }, []);

    if (stopProcessingUnit) {
        return null;
    }

    return (
        <div ref={ref}>
            <AdDisplayWidget
                adUnitRef={adUnitRef}
                width={width} 
                height={height}
                adError={adErrorState}
                adLoadFailed={adErrorState}
                registeredSlotId={registeredSlotId} 
                adLoaded={adLoadedState}
                showDefaultGDPRUser={showDefaultGDPRUser}
                adEnabled={enableAds} />
        </div>
    );
});

DisplayAdUnitWrapper.displayName = 'DisplayAdUnitWrapper';